import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getLatestArticles, getArticlesByCategory } from '@/mocks/articles';
import { useState, useEffect, useCallback } from 'react';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredArticles, setFilteredArticles] = useState(getLatestArticles(6));
  const [showAll, setShowAll] = useState(false);

  const latestArticles = getLatestArticles(6);
  const featuredArticles = getLatestArticles(3);
  const profileArticles = getArticlesByCategory('profile').slice(0, 2);
  const worksArticles = getArticlesByCategory('works');
  const photobookArticles = getArticlesByCategory('photobook').slice(0, 3);
  const newsArticles = getArticlesByCategory('news').slice(0, 3);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredArticles(getLatestArticles(6));
    } else {
      setFilteredArticles(getArticlesByCategory(activeCategory));
    }
  }, [activeCategory]);

  const displayedArticles = showAll ? filteredArticles : filteredArticles.slice(0, 6);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '泷泽萝拉是谁？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '泷泽萝拉（滝澤ローラ）是日本知名写真偶像与女演员，出生于东京都，拥有日俄混血血统，以精致的混血容貌和火辣的身材在亚洲娱乐圈广受欢迎。',
        },
      },
      {
        '@type': 'Question',
        name: '泷泽萝拉有哪些代表作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '泷泽萝拉的代表作品包含多部写真集和写真DVD，如《First Impression》《Sweet Devil》《Passion Red》等，同时也参演了多部日本电视剧和电影。',
        },
      },
      {
        '@type': 'Question',
        name: '泷泽萝拉的身高和三围是多少？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '泷泽萝拉身高172cm，三围为B88 / W58 / H85，罩杯为E Cup。',
        },
      },
      {
        '@type': 'Question',
        name: '如何获取泷泽萝拉的最新动态？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在本站"最新动态"栏目查看泷泽萝拉的最新活动和媒体报道，也可以关注她在Instagram、Twitter等社交平台的官方账号。',
        },
      },
      {
        '@type': 'Question',
        name: '泷泽萝拉的出道经历是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '泷泽萝拉于2012年在东京原宿街头被星探发掘，2013年推出首部个人写真作品正式出道，随后逐步拓展至影视、综艺等多个领域。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://jtdaifa.cn/api/search-image?query=Elegant%20Japanese%20female%20model%20silhouette%20on%20a%20warm%20minimalist%20backdrop%20with%20soft%20gradient%20lighting%2C%20cream%20and%20warm%20beige%20tones%2C%20artistic%20editorial%20photography%2C%20dreamy%20atmosphere%2C%20clean%20composition%2C%20high%20fashion%20aesthetic&width=1800&height=900&seq=takizawa-hero-01&orientation=landscape"
              alt="泷泽萝拉"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 tracking-wide">
                泷泽萝拉
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">
                日本知名写真偶像与女演员，官方资料、作品与最新动态一站全览
              </p>
              <div className="flex items-center gap-4 mt-8 flex-wrap justify-center">
                <Link
                  to="/category/profile"
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解泷泽萝拉
                </Link>
                <Link
                  to="/category/news"
                  className="px-6 py-2.5 bg-white/20 text-white border border-white/30 rounded-full text-sm font-label font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  最新动态
                </Link>
              </div>
            </div>
          </section>

          {/* Category Grid */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-12 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300 text-center cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl`} />
                  </div>
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-2 hidden md:block">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Articles */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                精选文章
              </h2>
              <Link
                to="/category/news"
                className="text-sm text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} variant="featured" />
              ))}
            </div>
          </section>

          {/* Two Column Layout: Profile + Works */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Profile Quick Info */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">
                    个人资料
                  </h2>
                  <Link
                    to="/category/profile"
                    className="text-sm text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer whitespace-nowrap"
                  >
                    详细资料 <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                    <div><span className="text-foreground-500">中文名：</span><span className="text-foreground-900 font-medium">泷泽萝拉</span></div>
                    <div><span className="text-foreground-500">日文名：</span><span className="text-foreground-900 font-medium">滝澤ローラ</span></div>
                    <div><span className="text-foreground-500">出生地：</span><span className="text-foreground-900 font-medium">日本 东京都</span></div>
                    <div><span className="text-foreground-500">出生日期：</span><span className="text-foreground-900 font-medium">1992年6月15日</span></div>
                    <div><span className="text-foreground-500">身高：</span><span className="text-foreground-900 font-medium">172cm</span></div>
                    <div><span className="text-foreground-500">三围：</span><span className="text-foreground-900 font-medium">B88 W58 H85</span></div>
                    <div><span className="text-foreground-500">星座：</span><span className="text-foreground-900 font-medium">双子座</span></div>
                    <div><span className="text-foreground-500">职业：</span><span className="text-foreground-900 font-medium">写真偶像 / 演员</span></div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-background-200/70">
                    {profileArticles.map((article) => (
                      <ArticleCard key={article.slug} article={article} variant="compact" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Works Quick Info */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">
                    作品全集
                  </h2>
                  <Link
                    to="/category/works"
                    className="text-sm text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer whitespace-nowrap"
                  >
                    全部作品 <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  {worksArticles.slice(0, 4).map((article) => (
                    <ArticleCard key={article.slug} article={article} variant="compact" />
                  ))}
                  {worksArticles.length > 4 && (
                    <p className="text-xs text-foreground-500 text-center mt-3">
                      还有更多作品...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Photobook + News */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">
                    写真集
                  </h2>
                  <Link
                    to="/category/photobook"
                    className="text-sm text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer whitespace-nowrap"
                  >
                    全部写真集 <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {photobookArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">
                    最新动态
                  </h2>
                  <Link
                    to="/category/news"
                    className="text-sm text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多动态 <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  {newsArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} variant="compact" />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Browse by Category - Interactive */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              按分类浏览文章
            </h2>
            <div className="flex items-center gap-1 mb-6 flex-wrap">
              <button
                onClick={() => { setActiveCategory('all'); setShowAll(false); }}
                className={`px-4 py-1.5 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-200/70 text-foreground-700 hover:bg-background-300'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => { setActiveCategory(cat.slug); setShowAll(false); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-200/70 text-foreground-700 hover:bg-background-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {displayedArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
            {filteredArticles.length > 6 && !showAll && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAll(true)}
                  className="px-6 py-2 border border-primary-300 text-primary-600 rounded-full text-sm font-label hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多 ({filteredArticles.length - 6} 篇)
                </button>
              </div>
            )}
          </section>

          {/* CTA Section */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-16 md:mt-20">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary-500/10 via-accent-100/50 to-primary-50/30 p-10 md:p-14 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                想了解更多关于泷泽萝拉？
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto mb-6">
                浏览泷泽萝拉最新动态，获取第一手资讯与独家内容
              </p>
              <Link
                to="/category/news"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看最新动态 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}